import { useQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSlug } from "@/hooks/useSlug";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { surveyMutations } from "@/queries/surveys/mutations";
import { useSurveyQueries } from "@/queries/surveys/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { type CreateOrEditSurveyFormValues, SurveyForm } from "./SurveyForm";

export function EditSurveyPage(): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSlug();
  const { id: surveyId } = useParams<{ id: string }>();
  const surveyQueries = useSurveyQueries();
  const communityGroupQueries = useCommunityGroupQueries();
  const surveyDetailsQuery = useQuery(
    surveyQueries.details({ surveyId: surveyId!, category: "other" }, { translationMode: "original" }),
  );
  const queryArgs = {
    Limit: 10000,
  } satisfies ApiQueryParams<"getGroupsV2">;
  const groupsQuery = useQuery(communityGroupQueries.list(queryArgs));
  const updateSurveyMutation = surveyMutations.useUpdateSurvey();

  const groups = useMemo(
    () => [
      { id: undefined, name: t("page.surveys.create-or-edit.form.group.general") },
      ...(groupsQuery.data ?? [])
        .filter((group) => group.canCreatePost || group.canCreateAnnouncement)
        .filter((group) => group.postingLevel !== "projectConnection")
        .map((group) => ({ id: group.id, name: group.name })),
    ],
    [t, groupsQuery.data],
  );
  const surveyDetails = surveyDetailsQuery.data;

  if (surveyDetailsQuery.isPending || groupsQuery.isPending) {
    return <FullSizeLoader withPadding />;
  }

  const error = surveyDetailsQuery.error || groupsQuery.error || surveyDetails?.type === "poll";
  if (error || !surveyDetails || !surveyId) {
    return <ErrorPage error={error ?? new Error("Survey not found")} />;
  }

  const allQuestions = surveyDetails.questionGroups.flatMap((group) => group.questions);
  const defaultFormValues: CreateOrEditSurveyFormValues = {
    type: surveyDetails.type,
    category: surveyDetails.category,
    startDate: surveyDetails.startsAt ? parseISO(surveyDetails.startsAt) : undefined,
    endDate: surveyDetails.endsAt ? parseISO(surveyDetails.endsAt) : undefined,
    group: surveyDetails.group ?? groups[0],
    title: surveyDetails.title,
    description: surveyDetails.description,
    thankYouMessage: surveyDetails.completedMessage,
    questions: allQuestions.map((question) => ({
      qId: question.id,
      type: question.type,
      question: question.question,
      options: question.answers?.map((option) => ({ oId: option.id, option: option.text })) || [],
      useForAnalysis: question.useForAnalysis,
    })),
    internal: surveyDetails.isInternal,
  };

  return (
    <SurveyForm
      isEditMode
      contentLanguageId={surveyDetails.originalLanguageId}
      defaultFormValues={defaultFormValues}
      groups={groups}
      onSubmit={async (payload) => {
        await updateSurveyMutation.mutateAsync({ surveyId, data: payload });
        void navigate(routes.surveys.details({ slug, id: surveyId }));
      }}
      isSubmitting={updateSurveyMutation.isPending}
      isPublished={surveyDetails.isPublished}
    />
  );
}
